import cn from "classnames";
import { useState } from "react";
import { Icons } from "@app/dashboard/src/components/shared";
import { BiuDetectorRowData, createEmptyDetectorRow, getTriggerTypeLabel, BiuNumber } from "./types";
import { BiuDetectorEditor } from "./BiuDetectorEditor";
import { getTargetTypeDisplayName, MaxServerIntValue, TargetType, enumKeys } from "@app/shared";

type BiuDetectorRowProps = {
    biuNum: BiuNumber;
    detectorNum: number;
    configurations: BiuDetectorRowData[];
    organizationId: string;
    facilityId?: string;
    luxcomEndpointId?: string;
    onSave: (data: BiuDetectorRowData, isNew: boolean) => Promise<void>;
    onDelete: (data: BiuDetectorRowData) => Promise<void>;
    isExpanded: boolean;
    onExpandChange: (isExpanded: boolean) => void;
    offlineMode?: boolean;
};

const formatTargetFilter = (targetFilter: number | undefined): string => {
    if (targetFilter === undefined || targetFilter === 0) return "-";
    if (targetFilter === MaxServerIntValue) return "All";

    const targets = enumKeys(TargetType)
        .filter((k) => k !== "Unknown" && (targetFilter & TargetType[k]) > 0)
        .map((k) => getTargetTypeDisplayName(k));

    if (targets.length === 0) return "-";
    if (targets.length > 2) return `${targets.slice(0, 2).join(", ")}...`;
    return targets.join(", ");
};

const formatZoneFilter = (data: BiuDetectorRowData): string => {
    const parts: string[] = [];
    if (data.detectionZoneName) parts.push(data.detectionZoneName);
    if (data.screenLineName) parts.push(data.screenLineName);
    return parts.length > 0 ? parts.join(", ") : "Any";
};

const formatDevice = (data: BiuDetectorRowData): string => {
    if (!data.deviceNames || data.deviceNames.length === 0) return "-";
    return data.deviceNames[0];
};

export const BiuDetectorRow: React.FC<BiuDetectorRowProps> = ({ biuNum, detectorNum, configurations, organizationId, facilityId, luxcomEndpointId, onSave, onDelete, isExpanded, onExpandChange, offlineMode }) => {
    const [editingData, setEditingData] = useState<BiuDetectorRowData | null>(null);
    const [isAddingNew, setIsAddingNew] = useState(false);

    const hasConfiguration = configurations.length > 0;
    const config = hasConfiguration ? configurations[0] : null;

    const handleRowClick = () => {
        if (hasConfiguration) {
            if (!isExpanded) {
                setEditingData({ ...config! });
                setIsAddingNew(false);
            }
            onExpandChange(!isExpanded);
        } else {
            handleAddNew();
        }
    };

    const handleAddNew = () => {
        const newData = createEmptyDetectorRow(biuNum, detectorNum, luxcomEndpointId);
        newData.targetFilter = MaxServerIntValue;
        setEditingData(newData);
        setIsAddingNew(true);
        onExpandChange(true);
    };

    const handleCancel = () => {
        setEditingData(null);
        setIsAddingNew(false);
        onExpandChange(false);
    };

    const handleSave = async () => {
        if (!editingData) return;
        await onSave(editingData, isAddingNew);
        setEditingData(null);
        setIsAddingNew(false);
        onExpandChange(false);
    };

    const handleDelete = async () => {
        if (!config) return;
        await onDelete(config);
        setEditingData(null);
        onExpandChange(false);
    };

    const handleEditorChange = (changes: Partial<BiuDetectorRowData>) => {
        if (editingData) {
            setEditingData({ ...editingData, ...changes });
        }
    };

    return (
        <div className={cn("border-b border-gray-200", { "bg-white": isExpanded })}>
            <div className={cn("flex items-center py-3 px-4 cursor-pointer transition-colors", hasConfiguration ? "hover:bg-gray-50" : "hover:bg-blue-50 text-gray-400")} onClick={handleRowClick}>
                <div className="w-6 mr-2">
                    <Icons.ChevronRight
                        className={cn("w-4 h-4 transition-transform", {
                            "rotate-90": isExpanded,
                            "text-gray-300": !hasConfiguration,
                        })}
                    />
                </div>

                <div className="w-16 font-medium">{detectorNum}</div>

                {hasConfiguration && config ? (
                    <div className={offlineMode ? "flex-1 grid grid-cols-4 gap-2 text-sm" : "flex-1 grid grid-cols-5 gap-2 text-sm"}>
                        <div className="truncate" title={config.expression}>
                            {config.expression || "-"}
                        </div>
                        <div className="truncate" title={getTriggerTypeLabel(config.triggerType)}>
                            {getTriggerTypeLabel(config.triggerType)}
                        </div>
                        <div className="truncate" title={formatZoneFilter(config)}>
                            {formatZoneFilter(config)}
                        </div>
                        {!offlineMode && (
                            <div className="truncate" title={formatDevice(config)}>
                                {formatDevice(config)}
                            </div>
                        )}
                        <div className="truncate" title={formatTargetFilter(config.targetFilter)}>
                            {formatTargetFilter(config.targetFilter)}
                        </div>
                    </div>
                ) : (
                    <div className="flex-1 text-sm italic">Click to configure detector {detectorNum}</div>
                )}
            </div>

            {isExpanded && editingData && <BiuDetectorEditor organizationId={organizationId} facilityId={facilityId} data={editingData} onChange={handleEditorChange} onSave={handleSave} onCancel={handleCancel} onDelete={hasConfiguration ? handleDelete : undefined} isNew={isAddingNew} offlineMode={offlineMode} />}
        </div>
    );
};
